/**
 * <cart-page> — AJAX cart page (mirrors the <cart-drawer> data flow).
 *
 * - Line mutations POST { id: <line key>, quantity } to {data-cart-change-url}
 *   (/cart/change.js); the response cart JSON is used to detect stock caps.
 * - Markup is re-rendered via the Section Rendering API:
 *   GET {data-section-url} ({cart_url}?sections={section.id}) and the
 *   [data-cart-page-content] subtree is swapped wholesale.
 * - Order notes save on change via {data-cart-update-url} (/cart/update.js).
 * - After every mutation a `cart:refresh` event is dispatched so the header
 *   count bubble ([data-cart-count]) and the <cart-drawer> stay in sync
 *   (cart-drawer.js owns updating [data-cart-count]).
 * - Live parity: when Shopify caps a requested quantity increase (all stock
 *   already in cart), the per-line "all stock in cart" error is revealed —
 *   live theme.js `Shopify.changeItem` behaviour.
 */
class CartPage extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('change', this.onChange.bind(this));
  }

  onClick(event) {
    const removeButton = event.target.closest('[data-line-remove]');
    if (removeButton) {
      this.changeLine(removeButton.dataset.lineKey, 0);
      return;
    }

    const qtyButton = event.target.closest('[data-qty-change]');
    if (qtyButton) {
      const input = this.querySelector(`[data-line-quantity][data-line-key="${qtyButton.dataset.lineKey}"]`);
      if (!input) return;
      // A cleared/garbled input parses to NaN — step from the last rendered value instead.
      const current = Number.isNaN(Number(input.value)) || input.value.trim() === ''
        ? Number(input.defaultValue)
        : Number(input.value);
      const quantity = Math.max(0, current + Number(qtyButton.dataset.qtyChange));
      this.changeLine(qtyButton.dataset.lineKey, quantity);
    }
  }

  onChange(event) {
    const input = event.target.closest('[data-line-quantity]');
    if (input) {
      // Blurring an emptied/non-numeric field must not silently delete the
      // line (Number('') === 0 → removal). Restore the rendered value.
      if (input.value.trim() === '' || Number.isNaN(Number(input.value))) {
        input.value = input.defaultValue;
        return;
      }
      this.changeLine(input.dataset.lineKey, Math.max(0, Number(input.value)));
      return;
    }

    const note = event.target.closest('[data-cart-note]');
    if (note) this.saveNote(note.value);
  }

  async changeLine(key, quantity) {
    if (!key) return;
    this.setBusy(true);

    try {
      const response = await fetch(this.dataset.cartChangeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      if (!response.ok) throw new Error(`Cart change failed: ${response.status}`);
      const cart = await response.json();

      await this.renderSection();

      // Stock cap: the returned line quantity is lower than requested.
      const line = cart.items.find((item) => item.key === key);
      if (line && quantity > line.quantity) {
        this.querySelector(`[data-line-error][data-line-key="${key}"]`)?.classList.remove('hidden');
      }

      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } catch (error) {
      console.error(error);
    } finally {
      this.setBusy(false);
    }
  }

  async saveNote(note) {
    try {
      await fetch(this.dataset.cartUpdateUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note }),
      });
    } catch (error) {
      console.error(error);
    }
  }

  async renderSection() {
    const response = await fetch(this.dataset.sectionUrl);
    const sections = await response.json();
    const sectionHtml = sections[this.dataset.sectionId];
    if (!sectionHtml) return;

    const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const newContent = parsed.querySelector('[data-cart-page-content]');
    const currentContent = this.querySelector('[data-cart-page-content]');
    if (newContent && currentContent) {
      // Preserve the open state of the order-notes disclosure across renders.
      if (currentContent.querySelector('.cart-page__notes[open]')) {
        newContent.querySelector('.cart-page__notes')?.setAttribute('open', '');
      }
      currentContent.replaceWith(newContent);
    }
  }

  setBusy(busy) {
    const content = this.querySelector('[data-cart-page-content]');
    content?.setAttribute('aria-busy', busy ? 'true' : 'false');
  }
}

if (!customElements.get('cart-page')) {
  customElements.define('cart-page', CartPage);
}
